import React from 'react';
import type { LookupTicketsOutput, SearchRelatedTicketsOutput } from '../../types/conciergeToolTypes';
import { SupportTicketCardChrome } from './SupportTicketCardChrome';
import type { SupportTicketCardStyles } from './supportTicketCardConfig';

type TicketsListOutput = LookupTicketsOutput | SearchRelatedTicketsOutput;

/**
 * Compact list of the user's open tickets, rendered as a column of small
 * rows. Used for the outputs of:
 *  - `tool-lookup_tickets`
 *  - `tool-search_related_tickets`
 */
export function SupportTicketsListPart({ output }: { output: TicketsListOutput | undefined }) {
  if (!output || output.status !== 'ok' || !output.tickets || output.tickets.length === 0) {
    return null;
  }

  return (
    <SupportTicketCardChrome>
      {(styles) => <SupportTicketsListView output={output} styles={styles} />}
    </SupportTicketCardChrome>
  );
}

function SupportTicketsListView({
  output,
  styles,
}: {
  output: TicketsListOutput;
  styles: SupportTicketCardStyles;
}) {
  const tickets = (output.tickets ?? []).slice(0, 10);

  return (
    <div
      className="tw:my-2 tw:border tw:shadow-sm"
      style={{
        borderRadius: styles.cardBorderRadius,
        borderColor: styles.cardBorderColor,
        backgroundColor: styles.cardBgColor,
        padding: styles.listCardPadding,
      }}
      role="group"
      aria-label="Your support tickets"
    >
      <div className="tw:mb-2 tw:flex tw:items-center tw:justify-between">
        <span
          className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide"
          style={{ color: styles.labelColor }}
        >
          Your tickets
        </span>
        <span className="tw:text-xs" style={{ color: styles.countColor }}>
          {output.tickets?.length}
        </span>
      </div>
      <div className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100">
        {tickets.map((t) => {
          const ref = t.displayId ?? t.ticketId;
          const ticketUrl = (t as any).ticketUrl;
          const title = (t as any).title ?? (t as any).summary ?? t.ticketTypeName ?? `Ticket #${ref}`;
          return (
            <div key={t.ticketId} className="tw:flex tw:items-center tw:justify-between tw:py-2 tw:gap-3">
              <div className="tw:flex tw:flex-col tw:min-w-0">
                <div className="tw:flex tw:items-center tw:gap-2">
                  <span
                    className="tw:font-semibold"
                    style={{ fontSize: styles.listRefFontSize, color: styles.refColor }}
                  >
                    #{ref}
                  </span>
                  <span
                    className={`tw:rounded-full tw:px-2 tw:py-0.5 tw:text-[10px] tw:font-medium ${stateClass(t.state)}`}
                    style={isDefaultState(t.state) ? defaultStateStyle : undefined}
                  >
                    {humanizeState(t.state)}
                  </span>
                </div>
                <div
                  className="tw:truncate"
                  style={{ fontSize: styles.metaFontSize, color: styles.metaColor }}
                >
                  {title}
                </div>
              </div>
              {ticketUrl && (
                <a
                  href={ticketUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw:shrink-0 tw:border tw:px-2 tw:py-1 tw:font-semibold tw:no-underline"
                  style={{
                    borderRadius: styles.viewBorderRadius,
                    borderColor: styles.viewBorderColor,
                    backgroundColor: styles.viewBgColor,
                    color: styles.viewTextColor,
                    fontSize: styles.viewFontSize - 1,
                  }}
                >
                  View
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function humanizeState(state: string): string {
  switch (state) {
    case 'submitted':
      return 'Submitted';
    case 'in_progress':
      return 'In progress';
    case 'waiting_on_customer':
      return 'Waiting on you';
    case 'resolved':
      return 'Resolved';
    case 'closed':
      return 'Closed';
    default:
      return state.replace(/_/g, ' ');
  }
}

function stateClass(state: string): string {
  switch (state) {
    case 'resolved':
    case 'closed':
      return 'tw:bg-gray-100 tw:text-gray-600';
    case 'waiting_on_customer':
      return 'tw:bg-amber-50 tw:text-amber-700';
    case 'in_progress':
      return 'tw:bg-blue-50 tw:text-blue-700';
    default:
      // D-33 — colors come from inline `defaultStateStyle` below so the brand
      // can re-skin via `--bc-support-bg` / `--bc-support-text`.
      return '';
  }
}

// D-33 — open/submitted ticket pill picks up the brand's support accent. The
// other semantic states (resolved/waiting/in_progress) keep their dedicated
// colors (gray/amber/blue) because those carry meaning beyond branding.
function isDefaultState(state: string): boolean {
  return state !== 'resolved' && state !== 'closed' && state !== 'waiting_on_customer' && state !== 'in_progress';
}

const defaultStateStyle: React.CSSProperties = {
  backgroundColor: 'var(--bc-support-bg)',
  color: 'var(--bc-support-text)',
};
